import { Fragment, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  HStack,
  LightModeSemanticColors,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  fontSizes,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { IconOnlyButton } from '~/components/common/IconOnlyButton'
import {
  AuditCell,
  DeleteIconButton,
  EditIconButton,
} from '~/components/common/MasterDataTable'
import {
  ROW_ACTION_COL_WIDTH,
  ROW_ACTION_GAP,
  STICKY_ACTION_HOVER_OVERLAY,
  stickyActionCellStyle,
  stickyActionHeaderStyle,
  useStickyRightShadow,
} from '~/components/common/MasterDataTable/stickyActionColumn'
import { RegionLoading } from '~/components/common/RegionLoading'
import { convertAreaLabel } from '~/models/area'
import type {
  TradingPartnerContactRow,
  TradingPartnerRow,
} from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/types'

type Props = {
  rows: TradingPartnerRow[]
  isLoading: boolean
  onEdit: (row: TradingPartnerRow) => void
  onDelete: (row: TradingPartnerRow) => void
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
}

// 親行の見出し数 (展開トグル + 取引先名称/略称/アカウント数/最終更新 4 列 + 操作列)。
const PARENT_COLUMN_COUNT = 6

// sticky header 背景 (éclair token: Surface.tableHeader、 MasterDataTable と同じ token)。
const TABLE_HEADER_BG = LightModeSemanticColors.Surface.tableHeader.value

const NO_VALUE = '—'

const thStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: TABLE_HEADER_BG,
  padding: '10px 12px',
  fontWeight: 600,
  fontSize: fontSizes.md.value,
  borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
  zIndex: 1,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  letterSpacing: 'normal',
  color: LightModeSemanticColors.Text.midEmphasis.value,
}

// 親行セルは中央揃え (design occto.html の ecl-table 準拠。 chevron IconButton で
// 行高が上がるため、 top 揃えだとテキストが行内で浮いて見える)。
const tdStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
  fontSize: fontSizes.md.value,
  verticalAlign: 'middle',
}

/**
 * アカウント 1 件の連絡先バケット (平日昼間 / 休日・夜間) セル。
 *
 * spec AC-T09/AC-T10 (AC-02): 連絡先名称 (department) / 担当者 (name) /
 * 電話番号 (phone) / メール (email) の全 4 項目を改行表示する。 name /
 * email は未設定のことがあるので、 設定されている行のみ描画する
 * (= 空行を作らない)。 複数連絡先は区切り線付きで縦に並べる。 バケットが
 * 空なら "—" を表示する。
 */
const ContactCell = ({
  contacts,
}: {
  contacts: TradingPartnerContactRow[]
}): React.ReactElement => {
  if (contacts.length === 0) {
    return (
      <span style={{ color: LightModeSemanticColors.Text.lowEmphasis.value }}>
        {NO_VALUE}
      </span>
    )
  }
  return (
    <Box>
      {contacts.map((c, idx) => (
        <Box
          key={c.id}
          marginTop={idx > 0 ? '4px' : 0}
          paddingTop={idx > 0 ? '4px' : 0}
          borderTop={
            idx > 0
              ? `1px dashed ${LightModeSemanticColors.Border.lowEmphasis.value}`
              : undefined
          }
        >
          <Box>{c.department !== '' ? c.department : NO_VALUE}</Box>
          {c.name !== undefined && c.name !== '' && <Box>{c.name}</Box>}
          <Box>{c.phone !== '' ? c.phone : NO_VALUE}</Box>
          {c.email !== undefined && c.email !== '' && <Box>{c.email}</Box>}
        </Box>
      ))}
    </Box>
  )
}

/**
 * 取引先マスタ一覧テーブル (spec AC-T09/AC-T10: 親子 2 階層展開構造、
 * EsljAccountTable.tsx の展開パターンを踏襲)。
 *
 * 親行: 取引先名称 / 略称 / アカウント数 / 最終更新 / 操作。 親行クリックで
 * 展開/折りたたみをトグルする (design occto.html 準拠)。 編集は操作列の
 * 編集ボタンからのみ起動する。
 *
 * 子行 (アカウント単位): エリア / 取引先コード (AC-02: 列見出しを「取引先
 * コード」に統一) / 取引先コード名称 / 平日昼間連絡先 / 休日・夜間連絡先。 デフォルトは
 * 全折りたたみ (design occto.html 準拠: state.expanded = {})。
 */
export const TradingPartnerTable = ({
  rows,
  isLoading,
  onEdit,
  onDelete,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // 展開済みの partner id 集合。 既定は空 (= 全折りたたみ、design occto.html 準拠)。
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // 操作列 sticky の shadow 出し分け (MasterDataTable と共通)。
  const { wrapRef, stickyShadow } = useStickyRightShadow([rows, expanded])

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const messageRow = (
    msg: string,
    color: string = LightModeSemanticColors.Text.midEmphasis.value,
  ): React.ReactElement => (
    <TableRow>
      <TableCell
        colSpan={PARENT_COLUMN_COUNT}
        style={{
          ...tdStyle,
          padding: '24px 12px',
          textAlign: 'center',
          color,
        }}
      >
        {msg}
      </TableCell>
    </TableRow>
  )

  return (
    <RegionLoading
      flexDirection="column"
      flex="1"
      display="flex"
      minHeight={isLoading ? '200px' : 0}
      isLoading={isLoading}
    >
      <Box
        ref={wrapRef}
        flex="1"
        overflowX="auto"
        overflowY="auto"
        background="Surface.primary"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="md"
      >
        <TableRoot style={{ width: '100%', borderCollapse: 'collapse' }}>
          <TableHeader>
            <TableRow>
              <TableColumnHeader style={{ ...thStyle, width: '36px' }} />
              <TableColumnHeader style={thStyle}>
                {t('page:adminOcctoMaster.tradingPartner.column.name')}
              </TableColumnHeader>
              <TableColumnHeader style={thStyle}>
                {t('page:adminOcctoMaster.tradingPartner.column.shortName')}
              </TableColumnHeader>
              <TableColumnHeader style={{ ...thStyle, textAlign: 'center' }}>
                {t('page:adminOcctoMaster.tradingPartner.column.accountCount')}
              </TableColumnHeader>
              <TableColumnHeader style={thStyle}>
                {t('page:adminOcctoMaster.tradingPartner.column.updatedAt')}
              </TableColumnHeader>
              <TableColumnHeader
                // 影は globalCss の `::before` が描く (border-collapse 表では
                // td/th 直当ての box-shadow が paint されない。 issue #5489)。
                data-sticky-shadow={stickyShadow}
                style={{
                  ...thStyle,
                  ...stickyActionHeaderStyle(TABLE_HEADER_BG),
                  textAlign: 'center',
                  width: ROW_ACTION_COL_WIDTH,
                }}
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? null
              : errorLabel !== undefined
                ? messageRow(
                    errorLabel,
                    LightModeSemanticColors.Text.negativeMid.value,
                  )
                : rows.length === 0
                  ? messageRow(t('common:table.empty'))
                  : rows.map((row, idx) => {
                      const isOpen = expanded.has(row.id)
                      const zebra =
                        idx % 2 === 1
                          ? LightModeSemanticColors.Surface.secondary.value
                          : undefined

                      return (
                        <Fragment key={row.id}>
                          {/* design occto.html 準拠: 行クリックで展開トグル、
                          操作列 (cell-actions) クリックは展開しない */}
                          <TableRow
                            background={zebra}
                            _hover={{
                              background: 'Hover.lowEmphasis',
                              ...STICKY_ACTION_HOVER_OVERLAY,
                            }}
                            onClick={() => toggle(row.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <TableCell
                              style={{ ...tdStyle, textAlign: 'center' }}
                            >
                              <IconOnlyButton
                                label={
                                  isOpen
                                    ? t(
                                        'page:adminOcctoMaster.tradingPartner.table.collapse',
                                      )
                                    : t(
                                        'page:adminOcctoMaster.tradingPartner.table.expand',
                                      )
                                }
                                size="md"
                                variant="ghost"
                              >
                                {isOpen ? (
                                  <FaChevronDownRegular />
                                ) : (
                                  <FaChevronRightRegular />
                                )}
                              </IconOnlyButton>
                            </TableCell>
                            <TableCell style={tdStyle}>{row.name}</TableCell>
                            <TableCell style={tdStyle}>
                              {row.shortName}
                            </TableCell>
                            <TableCell
                              style={{ ...tdStyle, textAlign: 'center' }}
                            >
                              {row.accounts.length}
                            </TableCell>
                            {/* 監査スタンプは 1 行に保つ (AuditCell は自前 nowrap を
                              持たず host 側で担保する契約)。 */}
                            <TableCell
                              style={{ ...tdStyle, whiteSpace: 'nowrap' }}
                            >
                              <AuditCell
                                updatedAt={row.updatedAt}
                                updatedBy={row.updatedBy}
                              />
                            </TableCell>
                            <TableCell
                              data-sticky-action
                              data-sticky-shadow={stickyShadow}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                ...tdStyle,
                                ...stickyActionCellStyle(idx),
                                width: ROW_ACTION_COL_WIDTH,
                              }}
                            >
                              <HStack
                                justifyContent="flex-end"
                                gap={ROW_ACTION_GAP}
                              >
                                <EditIconButton onClick={() => onEdit(row)} />
                                <DeleteIconButton
                                  onClick={() => onDelete(row)}
                                />
                              </HStack>
                            </TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow>
                              <TableCell
                                colSpan={PARENT_COLUMN_COUNT}
                                style={{
                                  padding: '0 0 0 36px',
                                  background:
                                    LightModeSemanticColors.Surface.secondary
                                      .value,
                                  borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
                                }}
                              >
                                {row.accounts.length === 0 ? (
                                  <Box
                                    padding="12px"
                                    color="Text.lowEmphasis"
                                    fontSize="md"
                                  >
                                    {t(
                                      'page:adminOcctoMaster.tradingPartner.table.noAccounts',
                                    )}
                                  </Box>
                                ) : (
                                  <Box
                                    display="grid"
                                    style={{
                                      gridTemplateColumns:
                                        '96px minmax(120px,1fr) minmax(130px,1.2fr) minmax(200px,2.2fr) minmax(200px,2.2fr)',
                                    }}
                                  >
                                    {/* grid ヘッダ (design: tp-cell-head = occto.html:73 md/600/mid-emphasis。textStyle="tableHeader" が size/weight exact、lineHeight 1.3 は role 既定) */}
                                    <Box
                                      textStyle="tableHeader"
                                      padding="6px 12px"
                                      color="Text.midEmphasis"
                                      background={TABLE_HEADER_BG}
                                    >
                                      {t(
                                        'page:adminOcctoMaster.tradingPartner.column.accountAreaCode',
                                      )}
                                    </Box>
                                    <Box
                                      textStyle="tableHeader"
                                      padding="6px 12px"
                                      color="Text.midEmphasis"
                                      background={TABLE_HEADER_BG}
                                    >
                                      {t(
                                        'page:adminOcctoMaster.tradingPartner.column.accountCode',
                                      )}
                                    </Box>
                                    <Box
                                      textStyle="tableHeader"
                                      padding="6px 12px"
                                      color="Text.midEmphasis"
                                      background={TABLE_HEADER_BG}
                                    >
                                      {t(
                                        'page:adminOcctoMaster.tradingPartner.column.accountCodeName',
                                      )}
                                    </Box>
                                    <Box
                                      textStyle="tableHeader"
                                      padding="6px 12px"
                                      color="Text.midEmphasis"
                                      background={TABLE_HEADER_BG}
                                    >
                                      {t(
                                        'page:adminOcctoMaster.tradingPartner.form.weekdayContacts',
                                      )}
                                    </Box>
                                    <Box
                                      textStyle="tableHeader"
                                      padding="6px 12px"
                                      color="Text.midEmphasis"
                                      background={TABLE_HEADER_BG}
                                    >
                                      {t(
                                        'page:adminOcctoMaster.tradingPartner.form.holidayContacts',
                                      )}
                                    </Box>
                                    {/* grid データ行 (design: tp-cell) */}
                                    {row.accounts.map((acc) => (
                                      <Fragment
                                        key={`${acc.areaCode}-${acc.code}`}
                                      >
                                        <Box
                                          padding="8px 12px"
                                          fontSize="md"
                                          borderBottom={`1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`}
                                        >
                                          {convertAreaLabel(acc.areaCode)}
                                        </Box>
                                        <Box
                                          padding="8px 12px"
                                          fontSize="md"
                                          borderBottom={`1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`}
                                          style={{
                                            fontVariantNumeric: 'tabular-nums',
                                          }}
                                        >
                                          {acc.code}
                                        </Box>
                                        <Box
                                          padding="8px 12px"
                                          fontSize="md"
                                          borderBottom={`1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`}
                                        >
                                          {acc.codeName !== ''
                                            ? acc.codeName
                                            : NO_VALUE}
                                        </Box>
                                        <Box
                                          padding="8px 12px"
                                          fontSize="md"
                                          borderBottom={`1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`}
                                        >
                                          <ContactCell
                                            contacts={acc.weekdayContacts}
                                          />
                                        </Box>
                                        <Box
                                          padding="8px 12px"
                                          fontSize="md"
                                          borderBottom={`1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`}
                                        >
                                          <ContactCell
                                            contacts={acc.holidayContacts}
                                          />
                                        </Box>
                                      </Fragment>
                                    ))}
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      )
                    })}
          </TableBody>
        </TableRoot>
      </Box>
    </RegionLoading>
  )
}
